import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Sun, Moon, Sparkles, ShieldCheck } from 'lucide-react';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { getUserFacingError } from '../../services/errorMessage';

const getLoginErrorMessage = (err) => {
  const status = err.response?.status;
  const genericMessage = getUserFacingError(err, 'Login failed');

  if (status === 401) {
    return genericMessage || 'Invalid credentials';
  }

  if (status === 403) {
    return genericMessage || 'Your account cannot sign in right now';
  }

  if (status >= 500) {
    return 'Unable to sign in right now. Please try again later.';
  }

  return genericMessage || 'Login failed';
};

const Login = () => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [info] = useState(location.state?.message || '');
  const [loading, setLoading] = useState(false);
  const [isCompact, setIsCompact] = useState(() => window.innerWidth < 900);

  const returnUrl = location.state?.returnUrl || searchParams.get('returnUrl');

  const accent = useMemo(
    () => (isDark ? 'linear-gradient(135deg, #84cc16 0%, #06b6d4 100%)' : 'linear-gradient(135deg, #65a30d 0%, #0284c7 100%)'),
    [isDark]
  );

  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 900);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);

      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      } else if (user.role === 'ADMIN') {
        navigate('/admin/meetings');
      } else if (user.role === 'SPEAKER') {
        navigate('/speaker/meetings');
      } else {
        navigate('/user/meetings');
      }
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: isDark
        ? 'radial-gradient(circle at 20% 10%, #1f2937 0%, transparent 35%), radial-gradient(circle at 90% 90%, #0f766e 0%, transparent 40%), #0b1220'
        : 'radial-gradient(circle at 15% 20%, #dcfce7 0%, transparent 35%), radial-gradient(circle at 88% 80%, #cffafe 0%, transparent 35%), #f8fafc',
      overflow: 'hidden',
    },
    themeToggle: {
      position: 'absolute',
      top: '24px',
      right: '24px',
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: theme.colors.backgroundSecondary,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: theme.colors.text,
      zIndex: 2,
      backdropFilter: 'blur(4px)',
    },
    auraLeft: {
      position: 'absolute',
      width: '280px',
      height: '280px',
      borderRadius: '999px',
      top: '-120px',
      left: '-80px',
      filter: 'blur(36px)',
      opacity: 0.32,
      background: accent,
    },
    auraRight: {
      position: 'absolute',
      width: '220px',
      height: '220px',
      borderRadius: '999px',
      bottom: '-80px',
      right: '-60px',
      filter: 'blur(34px)',
      opacity: 0.24,
      background: accent,
    },
    wrapper: {
      width: '100%',
      maxWidth: '1120px',
      padding: isCompact ? '20px' : '24px',
      position: 'relative',
      zIndex: 1,
    },
    shell: {
      display: 'grid',
      gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) minmax(360px, 420px)',
      alignItems: 'stretch',
      columnGap: isCompact ? '16px' : '24px',
    },
    leftPane: {
      padding: isCompact ? '20px 10px' : '40px 18px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '16px',
    },
    rightPane: {
      padding: isCompact ? 0 : '6px 0',
      display: 'flex',
      alignItems: 'center',
    },
    leftEyebrow: {
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: theme.colors.primary,
    },
    leftTitle: {
      fontSize: isCompact ? '32px' : '46px',
      lineHeight: '1.1',
      fontWeight: '800',
      letterSpacing: '-0.03em',
      color: theme.colors.text,
      margin: 0,
    },
    leftDescription: {
      fontSize: '16px',
      lineHeight: '1.7',
      color: theme.colors.textSecondary,
      maxWidth: '460px',
      margin: 0,
    },
    signalRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: '8px',
    },
    signal: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 11px',
      borderRadius: '999px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.textSecondary,
      fontSize: '12px',
      fontWeight: '600',
    },
    formColumn: {
      width: '100%',
    },
    authCard: {
      borderRadius: '16px',
      border: `1px solid ${theme.colors.border}`,
      boxShadow: '0 18px 32px rgba(15, 23, 42, 0.12)',
      backgroundColor: theme.colors.cardBg,
    },
    authCardBody: {
      padding: isCompact ? '22px' : '26px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      textAlign: 'left',
      marginBottom: '6px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      textAlign: 'left',
      marginBottom: '24px',
    },
    error: {
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.colors.error + '20',
      color: theme.colors.error,
      fontSize: '14px',
      marginBottom: '16px',
    },
    info: {
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.colors.primary + '12',
      color: theme.colors.primary,
      fontSize: '14px',
      marginBottom: '16px',
    },
    footer: {
      marginTop: '16px',
      textAlign: 'center',
      fontSize: '14px',
      color: theme.colors.textSecondary,
    },
    forgotRow: {
      marginTop: '-6px',
      marginBottom: '16px',
      textAlign: 'right',
      fontSize: '13px',
    },
    link: {
      color: theme.colors.primary,
      cursor: 'pointer',
      fontWeight: '500',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
    authBlockTitle: {
      fontSize: '13px',
      color: theme.colors.textSecondary,
      marginBottom: '6px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.auraLeft} />
      <div style={styles.auraRight} />

      <button
        style={styles.themeToggle}
        onClick={toggleTheme}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary;
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary;
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div style={styles.wrapper}>
        <div style={styles.shell}>
          <div style={styles.leftPane}>
            <div style={styles.leftEyebrow}>Welcome Back</div>
            <h1 style={styles.leftTitle}>Health Conference Portal</h1>
            <p style={styles.leftDescription}>
              Access your sessions, speaker tools, and conference operations in a calmer, faster workspace.
            </p>

            <div style={styles.signalRow}>
              <span style={styles.signal}>
                <ShieldCheck size={14} /> Secure access
              </span>
              <span style={styles.signal}>
                <Sparkles size={14} /> Role-based dashboard
              </span>
            </div>
          </div>

          <div style={styles.rightPane}>
            <div style={styles.formColumn}>
              <h1 style={styles.title}>Sign In</h1>
              <p style={styles.subtitle}>Continue to your conference account.</p>

              <Card cardStyle={styles.authCard} bodyStyle={styles.authCardBody}>
                <form onSubmit={handleSubmit}>
                  {info && <div style={styles.info}>{info}</div>}
                  {error && <div style={styles.error}>{error}</div>}

                  <div style={styles.authBlockTitle}>Account credentials</div>

                  <Input
                    type="email"
                    name="email"
                    label="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />

                  <Input
                    type="password"
                    name="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                  />

                  <div style={styles.forgotRow}>
                    <span style={styles.link} onClick={() => navigate('/forgot-password')}>
                      Forgot password?
                    </span>
                  </div>

                  <Button type="submit" fullWidth disabled={loading}>
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <div style={styles.footer}>
                  Don't have an account?{' '}
                  <span style={styles.link} onClick={() => navigate('/register')}>
                    Sign up
                  </span>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
